import { Inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelConstant } from '@components/chat/chat-list-header/constants/channels.constant';
import { JitsiIncomingCallComponent } from '@components/jitsi-incoming-call/jitsi-incoming-call.component';
import { ChannelTypeE } from '@enums/channel.enum';
import { ChatStatusE } from '@enums/chat-status.enum';
import { MessageEventsNameE } from '@enums/events/message-events-name';
import { ChatSocketService } from '@events/chat/chat-socket.service';
import { GroupSocketService } from '@events/chat/group-socket.service';
import { ChatHelperService } from '@helpers/chat-helper.service';
import { UserHelperService } from '@helpers/user-helper.service';
import { ChatI } from '@interfaces/chat.interface';
import { GroupI } from '@interfaces/group.interface';
import { MessageI, UpdateAllStatusMessageI } from '@interfaces/message.interface';
import { PageI } from '@interfaces/page.interface';
import { UserI } from '@interfaces/user.interface';
import { ChatService } from '@modules/chat/chat.service';
import { GroupService } from '@modules/chat/group.service';
import { ChatStoreService } from '@modules/chat/store/chat-store.service';
import { GroupStoreService } from '@modules/chat/store/group-store.service';
import { MediaMessagesGroupStoreService } from '@modules/chat/store/media-messages-group-store.service';
import { MediaMessagesStoreService } from '@modules/chat/store/media-messages-store.service';
import { MessageGroupStoreService } from '@modules/chat/store/message-group-store.service';
import { MessageStoreService } from '@modules/chat/store/message-store.service';
import { ChatStatusT } from '@omnitypes/chat.type';
import { MessagesService } from '@services/chat-persistence/messages.service';
import { distinctUntilChanged, filter, firstValueFrom, map, Subscription, switchMap, take } from 'rxjs';
import { UserPreferencesService } from '@modules/users/user.preferences.service';
import { DesktopNotificationService } from '@services/desktop-notification/desktop-notification.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ChatListHelperService } from '@helpers/chat-list-helper.service';
import { ChatFilterI } from '@interfaces/chat-filter.interface';
import { chatNotificationI } from '@store/chat/interface/chat-store.interface';
import { NavigationEnd, Router } from '@angular/router';
import { RoutesEnum } from '@enums/routes.enum';
import { Message } from '@node_modules/jssip/lib/Message';

@Injectable({
  providedIn: 'root',
})
export class ChatListenersService {
  private newChatSubscription?: Subscription;
  private loadChatSubscription?: Subscription;
  private newGroupSubscription?: Subscription;
  private syncGroupSubscription?: Subscription;
  private removeChatSubscription?: Subscription;
  private newMessageSubscription?: Subscription;
  private messageUpdatedOnCreateSubscription?: Subscription;
  private updateAllMessageToDeliveredSubscription?: Subscription;
  private updateAllMessageGroupSubscription?: Subscription;
  private messageUpdatedSubscription?: Subscription;
  private updateMessageSubscription?: Subscription;
  private updateMessageGroupSubscription?: Subscription;
  private updateAllMessageSubscription?: Subscription;
  private newGroupMessageSubscription?: Subscription;
  private updateGroupSubscription?: Subscription;
  private deleteGroupSubscription?: Subscription;
  private groupCreateFailedSubscription?: Subscription;
  private updateGroupParticipantsSubscription?: Subscription;
  private changedContactChatSubscription?: Subscription;
  private createGroupStepCallInitiatedSubscription?: Subscription;
  private createChatStepCallInitiatedSubscription?: Subscription;
  private newChatStepSubscription?: Subscription;
  constructor(
    @Inject(ChatSocketService) private chatSocketService: ChatSocketService,
    @Inject(GroupSocketService) private groupSocketService: GroupSocketService,
    private mediaMessageStoreService: MediaMessagesStoreService,
    private mediaMessagesGroupStoreService: MediaMessagesGroupStoreService,
    private chatStoreService: ChatStoreService,
    private userHelperService: UserHelperService,
    private messageStoreService: MessageStoreService,
    private messageGroupStoreService: MessageGroupStoreService,
    private groupStoreService: GroupStoreService,
    private chatHelperService: ChatHelperService,
    private messagesService: MessagesService,
    private chatService: ChatService,
    private readonly groupService: GroupService,
    private readonly dialog: MatDialog,
    private userPreferencesService: UserPreferencesService,
    private chatListHelperService: ChatListHelperService,
    private router: Router,
    private readonly desktopNotificationService: DesktopNotificationService,
    private readonly translateService: TranslateService,
    private readonly injector: Injector
  ) {}

  // Resolved lazily: this service is built during APP_INITIALIZER, and an eager ToastrService freezes an invalid ApplicationRef (OMNI-7031/7923).
  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  async initialize() {
    this.loadChatEvents();
    this.listenNewChat();
    this.listenLoadChat();
    this.listenNewGroup();
    this.listenMessageUpdate();
    this.listenRemovedChat();
    this.firstLoadNotifications();
    this.listenUpdateGroup();
    this.listenDeleteGroup();
    this.listenGroupCreateFailed();
    this.listenUpdateGroupParticipants();
    this.listenChangedContact();
    this.listenCreateGroupStepCallInitiated();
    this.listenCreateChatStepCallInitiated();
    this.listenNewChatStep();
  }

  private async loadChatEvents(): Promise<void> {
    this.addNewMessage();
    this.listenChatType();
  }

  private listenChatType() {
    this.chatStoreService.chatType$.pipe(distinctUntilChanged()).subscribe(async () => {
      this.chatStoreService.updateTotalNotificationBySelectedType();
      await this.chatStoreService.unsetCurrentChat();
    });
  }

  private async removeChatFromArchive(message: MessageI) {
    if (!message?.chat || message?.sender?.type !== 'contact') {
      return;
    }

    const customerId = this.userHelperService.getUserAttributes()['custom:customer'];
    const toUnarchive = message?.chatStatus === ChatStatusE.ATTENDANCE && message?.customerId === customerId;

    const chat = await this.chatStoreService.existsChatInStore({ _id: message.chat! });

    if (chat?.status === ChatStatusE.ARCHIVED && toUnarchive) {
      this.chatStoreService.removeChatByType(message.chat!, ChatStatusE.ARCHIVED);
      const unarchivedChat = await firstValueFrom(this.chatService.findChatById(message.chat!));
      return await this.updateChatOnAttendance(unarchivedChat);
    }
  }

  private addNewMessage(): void {
    this.newMessageSubscription?.unsubscribe();
    this.newMessageSubscription = this.chatSocketService.newMessage$.subscribe(async (message) => {
      // The 'message' payload carries no agent, so a message broadcast to the team room for a chat already
      // reassigned to another agent would resurface it in this agent's list. Validate against the stored
      // chat and evict instead of bubbling when it belongs to another agent and is gate-denied.
      const storedChat = message?.chat
        ? await this.chatStoreService.existsChatInStore({ _id: message.chat })
        : null;
      if (storedChat && this.isAssignedToOtherAgent(storedChat) && !(await this.canShowChat(storedChat))) {
        this.removeChatStatusList(storedChat);
        return;
      }
      this.chatStoreService.addMentionOnChat(message);
      if (message && (message as any)?.type === 'stepEndSession') {
        this.chatStoreService.addStepInChat(message.chat!, message);
      }
      const newMessageExistsInStore = await this.messageStoreService.existsMessageInStore(
        message?.chat!,
        message?._id!
      );
      const oldMessageExistsInStore = await this.messageStoreService.existsMessageInStore(
        message?.chat!,
        message?.oldId!
      );
      const messageExistsInStore = newMessageExistsInStore || oldMessageExistsInStore;
      const isMessageSendFromMe =
        message && message.oldId && this.userHelperService.verifyIfUserIsCurrentUser(message?.sender?._id);
      if (message?._id && !messageExistsInStore) {
        if (!message.createdAt) {
          message = {
            ...message,
            createdAt: new Date().toISOString(),
          };
        }
        void this.chatStoreService.updateLatestMessage(message);
        this.messageStoreService.addMessage(message.chat!, message);
        this.mediaMessageStoreService.pushMessageIfIsMedia(message.chat!, message);

        if (
          message &&
          message.chat &&
          message.channelType &&
          message.status !== 'read' &&
          message?.sender?.type === 'contact'
        ) {
          this.chatStoreService.updateCountStateByType(message.chat, undefined, false, true);
          this.chatStoreService.setNotificationsOnNewMessage(message);
          this.notifyDesktopNewMessage(message);
        }
        // Internal notes travel on the same event but with an agent sender and INTERNAL channel,
        // so they never reach the contact-message branch above.
        if (
          message.channelType === ChannelTypeE.INTERNAL &&
          !this.userHelperService.verifyIfUserIsCurrentUser(message?.sender?._id)
        ) {
          this.notifyDesktopInternalNote(message);
        }
        if (message.status === 'sent') {
          this.messageStoreService.updateAllMessageStatusByChat(message.chat!, 'read');
        }
      }

      if (message?._id) {
        const chatStatus =
          message.channelType && message.channelType === ChannelTypeE.INTERNAL
            ? ChatStatusE.INTERNAL
            : ChatStatusE.ATTENDANCE;

        this.chatStoreService.addMessageInChatById(message.chat!, message, chatStatus);
        this.chatStoreService.updateLatestMessage(message);

        if (messageExistsInStore && isMessageSendFromMe) {
          const existingMsg = await firstValueFrom(
            this.messageStoreService.getMessageById(message.chat!, message.oldId || message._id!)
          );
          if (existingMsg) {
            message = { ...message, time: existingMsg.time, createdAt: existingMsg.createdAt };
          }
          this.messageStoreService.updateMessageById(message.chat!, message._id!, message);
        } else if (messageExistsInStore) {
          this.messageStoreService.updateMessageById(message.chat!, message._id!, message);
        }
      }

      message && this.removeChatFromArchive(message);
    });

    this.newGroupMessageSubscription?.unsubscribe();
    this.newGroupMessageSubscription = this.groupSocketService.newMessage$.subscribe(async (data) => {
      if (data) {
        const groupId = data.message?.group!.toString();

        const newMessageExistsInStore = !!(await this.messageGroupStoreService.getMessageById(
          groupId,
          data.message?._id!
        ));

        const oldMessageExistsInStore = !!(await this.messageGroupStoreService.getMessageById(groupId, data.oldId!));

        const messageExistsInStore = newMessageExistsInStore || oldMessageExistsInStore;

        if (!messageExistsInStore) {
          this.messageGroupStoreService.addMessage(groupId, data.message);
          this.notifyDesktopGroupMessage(groupId, data.message);
        } else {
          this.messageGroupStoreService.updateMessageById(groupId, data.oldId!, data.message);
        }

        this.groupStoreService.addLastMessage(groupId, data.message);
        this.groupStoreService.addMessageInGroup(groupId, data.message);
        this.mediaMessagesGroupStoreService.pushMessageGroupIfIsMedia(groupId, data.message);
      }
    });
  }

  private async updateChatOnAttendance(chat: ChatI, fromPartial?: boolean) {
    if (!chat || (!fromPartial && (await this.myChatExistsInStore(chat)))) {
      return;
    }

    if (await this.canShowChat(chat)) {
      !fromPartial && this.removeChatStatusList(chat);

      if (!!chat.channel) {
        this.addOnChat(chat, fromPartial);
        this.chatSocketService.joinInNewChatRoom(chat);
      }
    } else {
      if (!fromPartial || this.isAssignedToOtherAgent(chat)) {
        this.removeChatStatusList(chat);
      }
    }
  }

  // A partial (chat-data) event normally must not evict — its payload can be incomplete and a blind
  // removal would drop the agent's own chats. Exception: a chat whose agent is someone other than the
  // current user and that canShowChat already denied was reassigned to another agent's carteira; evict
  // it even on a partial event, otherwise a stale entry from the shared team-queue phase lingers and
  // re-surfaces on the next inbound message, letting an agent see/answer another agent's chat.
  private isAssignedToOtherAgent(chat: ChatI): boolean {
    const agentId = (chat.agent as any)?._id ?? (chat.agent as unknown as string);
    return !!agentId && !this.userHelperService.verifyIfUserIsCurrentUser(agentId);
  }

  private removeChatStatusList(chat: ChatI) {
    if (chat._id) {
      const statusList = [
        ChatStatusE.QUEUE,
        ChatStatusE.ATTENDANCE,
        ChatStatusE.INTERNAL,
        ChatStatusE.FINISHED,
        ChatStatusE.ARCHIVED,
        ChatStatusE.STARTFLOW,
        ChatStatusE.API,
        ChatStatusE.DELAYED,
        ChatStatusE.NEW,
        ChatStatusE.TRANSFER,
        ChatStatusE.WAITING,
      ] as ChatStatusT[];

      for (const status of statusList) {
        this.chatStoreService.removeChatByType(chat._id, status);
        this.chatStoreService.unsetCurrentChatIfCurrent(chat._id);
      }
    }
  }

  private listenNewChat(): void {
    this.newChatSubscription?.unsubscribe();
    this.newChatSubscription = this.chatSocketService.newChat$
      .pipe(
        switchMap(async (newChat: ChatI | null) => {
          if (newChat) {
            return await this.updateChatOnAttendance(newChat);
          }
          return null;
        })
      )
      .subscribe();
  }

  private listenLoadChat(): void {
    this.loadChatSubscription?.unsubscribe();
    this.loadChatSubscription = this.chatSocketService.loadChatData$
      .pipe(
        switchMap(async (chat: ChatI | null) => {
          if (chat) {
            return await this.updateChatOnAttendance(chat, true);
          }
          return null;
        })
      )
      .subscribe();
  }

  private async canShowChat(chat: ChatI) {
    return (
      this.showChatCurrentCustomer(chat) &&
      (this.showChatCurrentUser(chat) ||
        this.showChatInternal(chat) ||
        this.showChatByTeam(chat) ||
        this.showChatFlow(chat) ||
        this.userHelperService.isAdminOrHigher())
    );
  }

  private async myChatExistsInStore(chat) {
    const chatExist = await this.chatStoreService.existsChatInStore(chat);
    const userId = this.userHelperService.getUserAttributes()['custom:userId'];

    const attendanceExists =
      chatExist &&
      chatExist?.status === 'attendance' &&
      chat?.agent?._id === userId &&
      chatExist?.agent?._id === userId &&
      chat.status !== 'delayed';

    const internalExists =
      chatExist && chatExist?.status === 'internal' && chatExist.internalAgents.find((agent) => agent._id === userId);

    if (attendanceExists || internalExists) {
      return true;
    }
    return false;
  }

  private async addOnChat(transferedChat: ChatI, fromPartial?: boolean) {
    if (this.chatListHelperService.filterChat(transferedChat)) {
      const joinedChatIds = this.chatSocketService.getJoinedChatIds();
      this.populateWithIconAndPhoto(transferedChat, joinedChatIds, fromPartial);
    } else {
      this.removeChatStatusList(transferedChat);
    }

    if (!fromPartial && (this.showChatCurrentUser(transferedChat) || this.showChatInternal(transferedChat))) {
      const inQueue = this.chatHelperService.isInQueueChat(transferedChat);
      await this.chatStoreService.addNotification(transferedChat._id!, transferedChat.status as ChatStatusE, inQueue);
      this.notifyDesktopNewAttendance(transferedChat);
    }
  }

  private showChatInternal(chat) {
    return chat.status === 'internal' && !!this.chatHelperService.getSelfInternalAgentFromChat(chat);
  }

  private showChatCurrentCustomer(chat) {
    const customerId = this.userHelperService.getUserAttributes()['custom:customer'];
    return chat.customerId === customerId;
  }

  private showChatCurrentUser(chat) {
    const userId = this.userHelperService.getUserAttributes()['custom:userId'];
    const currentUser = chat.agent && this.userHelperService.verifyIfUserIsCurrentUser(chat.agent._id ?? chat.agent);
    const currentUserArray = !chat.agent && chat.agents && chat.agents.indexOf(userId) >= 0;
    return currentUser || currentUserArray || this.showChatMentionUser(chat);
  }

  private showChatMentionUser(chat: ChatI): boolean {
    const userId = this.userHelperService.getUserAttributes()['custom:userId'];

    if (chat.mentions && chat.mentions.length > 0) {
      return !!chat.mentions.find((mention) => mention.agent?._id === userId && mention.count > 0);
    }

    return false;
  }

  private showChatFlow(chat) {
    const customerId = this.userHelperService.getUserAttributes()['custom:customerId'];
    return chat.status === 'start_flow' && chat.customerId === customerId;
  }

  private showChatByTeam(chat) {
    if (!chat?.team) {
      return;
    }
    const userId = this.userHelperService.getUserAttributes()['custom:userId'];
    const currentTeam = this.userHelperService.verifyIfTeamIsCurrentTeam(chat.team!._id);
    const supervisorTeam = chat.team?.supervisors && chat.team?.supervisors.indexOf(userId) >= 0;
    const isAgentOnTeam = chat.team?.agents && chat.agent?._id && chat.team?.agents.indexOf(chat.agent?._id) >= 0;
    let result = (currentTeam && !chat.agent) || (currentTeam && supervisorTeam && isAgentOnTeam);
    if (!!supervisorTeam) {
      result = !!supervisorTeam;
    }
    const otherTeams: string[] = this.chatStoreService.otherTeams ?? [];
    if (otherTeams?.length && !result) {
      return otherTeams.includes(chat?.team?._id ?? ``);
    }
    return result;
  }

  private async populateWithIconAndPhoto(chat: ChatI, joinedChatIds: string[], fromPartial?: boolean) {
    chat = this.populateIcon(chat);
    chat = await this.populatePhoto(chat);
    this.chatStoreService.populateStore([chat], true, true);

    if (
      fromPartial ||
      ![ChatStatusE.STARTFLOW, ChatStatusE.NEW].includes(chat.status as ChatStatusE) ||
      !joinedChatIds.includes(chat._id!)
    ) {
      this.loadInitialMessages([chat]);
    }
  }

  private populateIcon(chat: ChatI) {
    if (!chat.channel || typeof chat.channel === 'string') {
      return chat;
    }
    const channel = ChannelConstant.find((c) => c.name === chat.channel?.type)!;
    chat.channel.icon = channel?.icon;
    chat.channel.class = channel?.class;
    return chat;
  }

  private async populatePhoto(chat: ChatI) {
    if (chat.contact?.avatar) {
      this.chatStoreService.addContactPhoto(chat.contact!._id!, chat.contact?.avatar);
    }
    return chat;
  }

  private loadInitialMessages(chats: ChatI[]) {
    const pages: { chat: string; page: PageI<MessageI> }[] = [];
    chats.forEach((chat) => {
      const defaultNonEmptyPage = {
        meta: {
          page: 1,
          limit: 50,
          itemCount: 0,
          hasPreviousPage: false,
          hasNextPage: !!chat.lazyLoadMessages,
          initialLazyLoadMessages: true,
        },
        data: chat?.messages,
      } as PageI<MessageI>;

      const defaultEmptyPage = {
        data: [],
        meta: {
          page: 1,
          limit: 50,
          itemCount: 0,
          pageCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        },
      } as PageI<MessageI>;
      const messagesPage: PageI<MessageI> = !chat?.messages ? defaultEmptyPage : defaultNonEmptyPage;
      pages.push({ chat: chat._id!.toString(), page: messagesPage });
    });
    this.messageStoreService.populateOrUnshiftMessagesByChatIds(pages);
  }
  private listenRemovedChat(): void {
    this.removeChatSubscription?.unsubscribe();
    this.removeChatSubscription = this.chatSocketService.removeChat$.subscribe((chat) => {
      if (!chat || !chat._id || !chat.status) return;
      this.chatStoreService.removeChat(chat._id);
    });
  }

  private listenNewGroup(): void {
    this.newGroupSubscription?.unsubscribe();
    this.newGroupSubscription = this.groupSocketService.newGroup$.subscribe((group) => {
      if (group?._id) {
        this.groupStoreService.populateStore([group]);
        this.groupSocketService.listAllGroupRooms([group]);
      }
    });

    this.syncGroupSubscription?.unsubscribe();
    this.syncGroupSubscription = this.groupSocketService.syncGroup$.subscribe((status) => {
      if (status) {
        this.groupStoreService.setSyncGroup(status === 'started');
      }
    });
  }

  private listenMessageUpdate() {
    this.messageUpdatedOnCreateSubscription?.unsubscribe();
    this.messageUpdatedOnCreateSubscription = this.chatSocketService.messageUpdatedOnCreate$.subscribe(
      async (message) => {
        if (message?.chatId && message?.oldId && message?._id) {
          this.messageStoreService.updateMessageId(message.chatId, message.oldId, message._id);
          this.messageStoreService.updateMessageStatusById(message.chatId, message._id, message.status);
          const latestMessageByChat = await firstValueFrom(this.chatStoreService.getLatestMessageById(message.chatId));
          if (
            latestMessageByChat &&
            (latestMessageByChat._id === message._id || latestMessageByChat._id === message.oldId)
          ) {
            this.chatStoreService.updateLatestMessage({ ...latestMessageByChat, status: message.status });
          }
        }
      }
    );

    this.updateMessageSubscription?.unsubscribe();
    this.updateMessageSubscription = this.chatSocketService.updateMessage$.subscribe((message) => {
      if (message?.status === 'fail') {
        this.messageStoreService.updateMessageFailStatusById(
          message.chat,
          message._id,
          message.dataFail!,
          message.errors
        );
        return;
      }
      if (message?.session && message?.chat) {
        this.chatStoreService.updateSessionFromMessage(message?.chat, message?.session);
      }
      if (!message || !message._id) return;
      const { chatStatus, chat, _id, status, errors } = message;
      this.chatStoreService.updateMessageByChatStatus(chatStatus, chat, _id, status, errors);
      this.messageStoreService.updateMessageStatusById(chat, _id, status);
    });

    this.updateAllMessageSubscription?.unsubscribe();
    this.updateAllMessageSubscription = this.chatSocketService.updateAllMessage$.subscribe(async (data) => {
      if (!data) return;

      if (!data.eventType) {
        this.messageStoreService.updateAllMessageStatusByChat(data.chat, data.status, true, data.type);
        this.messageStoreService.updateLastMessageStatusByChat(data.chat, data.status, data.type);
        const latestMessageByChat = await firstValueFrom(this.chatStoreService.getLatestMessageById(data.chat));
        if (latestMessageByChat && data.status) {
          this.chatStoreService.updateLatestMessage({ ...latestMessageByChat, status: data.status });
        }
      } else {
        this.updateMessageByEventType(data);
      }
    });

    this.updateAllMessageToDeliveredSubscription?.unsubscribe();
    this.updateAllMessageToDeliveredSubscription = this.chatSocketService.updateAllMessageToDelivered$.subscribe(
      (data) => {
        if (!data) return;
        const { chat, type } = data;
        this.chatStoreService.updateMessageStatusAsDeliveredByChatId(chat, type, true);
      }
    );

    this.updateMessageGroupSubscription?.unsubscribe();
    this.updateMessageGroupSubscription = this.groupSocketService.updateMessage$.subscribe((message) => {
      if (!message || !message._id) return;
      this.messageGroupStoreService.updateMessageById(message.group, message._id, message);
    });

    this.updateAllMessageGroupSubscription?.unsubscribe();
    this.updateAllMessageGroupSubscription = this.groupSocketService.updateAllMessage$.subscribe(async (data) => {
      if (data?.eventType === MessageEventsNameE.DELETE && data?.messageId) {
        this.messageGroupStoreService.updateMessageDeleteStatusById(data.group, data.messageId);
      } else if (data?.eventType === MessageEventsNameE.TRANSCRIPTION && data?.messageId) {
        this.messageGroupStoreService.updateMessageTranscriptionById(data.group, data.messageId, data.transcription);
      }
    });

    this.messageUpdatedSubscription?.unsubscribe();
    this.messageUpdatedSubscription = this.chatSocketService.updatedMessage$.subscribe(
      async (message: MessageI | null) => {
        if (message) {
          this.messageStoreService.updateMessageById(message.chat!, message._id!, message);
          const latestMessageByChat = await firstValueFrom(this.chatStoreService.getLatestMessageById(message.chat!));
          if (latestMessageByChat && latestMessageByChat?._id === message?._id) {
            this.chatStoreService.updateLatestMessage({ ...latestMessageByChat, ...message });
          }
        }
      }
    );
  }

  private async updateMessageByEventType(data: UpdateAllStatusMessageI) {
    switch (data.eventType) {
      case MessageEventsNameE.DELETE: {
        this.messageStoreService.updateMessageDeleteStatusById(data.chat, data.messageId!);
        // OMNI-6209: refletir a exclusão no preview da sidebar quando a msg apagada
        // (vinda do WhatsApp) é a última do chat (latestMessage).
        const latestMessageByChat = await firstValueFrom(this.chatStoreService.getLatestMessageById(data.chat));
        if (latestMessageByChat && String(latestMessageByChat._id) === String(data.messageId)) {
          this.chatStoreService.updateLatestMessage({ ...latestMessageByChat, deletedMessage: true });
        }
        break;
      }
      case MessageEventsNameE.TRANSCRIPTION:
        this.messageStoreService.updateMessageTranscriptionById(data.chat, data.messageId!, data.transcription!);
        break;
      case MessageEventsNameE.CANCEL:
        if (data.latestMessage) {
          this.chatStoreService.updateLatestMessage(data.latestMessage);
        } else {
          this.chatStoreService.removeLatestMessage(data.chat);
        }
        this.messageStoreService.removeMessage(data.chat, data.messageId!);
        break;
    }
  }

  private async firstLoadNotifications() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        const currentUrl = this.router.url;

        if (!currentUrl.includes(`/${RoutesEnum.PANEL}/${RoutesEnum.CHAT}`)) {
          this.loadNotificationData();
        }
      });
  }

  private async loadNotificationData() {
    const { data } = await this.allAttendanceChats();
    this.chatStoreService.populateStore(data, true);
    this.chatSocketService.listAllChatsRooms(data);
    this.loadInitialMessages(data);
    await firstValueFrom(this.chatStoreService.chats$);
  }

  private firstLoadUnreadChatsCount() {
    this.chatService.getUnreadedChatsCount().then((data: chatNotificationI[]) => {
      if (data) {
        this.chatStoreService.setNotification(data);
      }
    });
  }

  private async allAttendanceChats(): Promise<PageI<ChatI>> {
    const preferences = await this.userPreferencesService.getPreferences();
    let filter: ChatFilterI;

    if (preferences) {
      filter = this.chatListHelperService.createChatFiltersByUserPreferences(preferences);
    } else {
      filter = this.chatStoreService.getCurrentChatFilterValues();
    }
    const sort = { latestMessageCreatedAt: -1, createdAt: -1 };
    const user = this.userHelperService.getSocketLoginData()!;

    return await firstValueFrom(this.chatService.getAllChatsByAllTeams(user, 1, 50, '', filter, sort));
  }

  private listenUpdateGroup(): void {
    this.updateGroupSubscription?.unsubscribe();
    this.updateGroupSubscription = this.groupSocketService.updateGroup$.subscribe((group: GroupI | null) => {
      if (group) {
        this.groupStoreService.populateStore([group]);
      }
    });
  }

  private async populateGroupInitialMessages(groups: GroupI[]) {
    const fetchMessagesPromises = groups.map(async (group) => {
      const messagesPage = !group.messages
        ? await firstValueFrom(this.groupService.getGroupMessages(group._id!, 1))
        : {
            meta: {
              page: 1,
              limit: 50,
              itemCount: 0,
              hasPreviousPage: false,
              hasNextPage: !!group.lazyLoadMessages,
              initialLazyLoadMessages: true,
            },
            data: group.messages,
          };

      this.messageGroupStoreService.populateOrUnshiftMessagesByChatId(group._id!, messagesPage);
    });

    await Promise.all(fetchMessagesPromises);
  }

  private listenDeleteGroup(): void {
    this.deleteGroupSubscription?.unsubscribe();
    this.deleteGroupSubscription = this.groupSocketService.deleteGroup$.subscribe((groupId: string) => {
      if (groupId) {
        this.groupStoreService.removeGroup(groupId);
      }
    });
  }

  private listenGroupCreateFailed(): void {
    this.groupCreateFailedSubscription?.unsubscribe();
    this.groupCreateFailedSubscription = this.groupSocketService.groupCreateFailed$.subscribe((group) => {
      this.toastr.error(this.translateService.instant('GROUP_CREATE_FAILED', { subject: group?.subject ?? '' }));
    });
  }

  private listenUpdateGroupParticipants(): void {
    const user = this.userHelperService.getSocketLoginData()!;

    this.updateGroupParticipantsSubscription?.unsubscribe();
    this.updateGroupParticipantsSubscription = this.groupSocketService.updateGroupParticipants$.subscribe(
      async (groupId) => {
        if (groupId) {
          const group = await firstValueFrom(this.groupService.findOne(groupId));
          const participants = await this.groupService.getAllGroupParticipants(group);

          if (
            !participants?.find(
              (participant) => (participant.user as UserI)?._id === user._id && !participant.isDeleted
            ) &&
            !group.agents?.find((agent) => agent._id === user._id)
          ) {
            this.groupStoreService.removeGroup(groupId);
          } else {
            this.groupStoreService.populateStore([group]);
            this.groupSocketService.listAllGroupRooms([group]);
            this.populateGroupInitialMessages([group]);
          }
        }
      }
    );
  }

  private listenChangedContact(): void {
    this.changedContactChatSubscription?.unsubscribe();
    this.changedContactChatSubscription = this.chatSocketService.changedContact$.subscribe(
      async (chat: ChatI | null) => {
        if (!chat || !chat._id || !chat.contact?._id) {
          return;
        }

        const oldContact = await firstValueFrom(
          this.chatStoreService.flow$.pipe(
            map((flowChats) => flowChats.find((flowChat) => flowChat._id === chat._id)),
            filter((flowChat) => !!flowChat),
            map((flowChat) => flowChat!.contact || undefined)
          )
        );

        if (oldContact?._id && oldContact._id !== chat.contact._id) {
          await this.chatStoreService.replaceFlowChatContact(chat._id, chat.contact);
          this.chatStoreService.changeContact(chat);
          await this.chatStoreService.updateContactInAllChats(chat.contact);
        }
      }
    );
  }

  private listenCreateGroupStepCallInitiated() {
    this.createGroupStepCallInitiatedSubscription?.unsubscribe();
    this.createGroupStepCallInitiatedSubscription =
      this.groupSocketService.createGroupStepCallInitiatedSubject$.subscribe((step) => {
        if (step) {
          this.dialog.open(JitsiIncomingCallComponent, {
            panelClass: 'custom-dialog-v2',
            hasBackdrop: false,
            data: {
              avatarUrl: step?.agent?.avatarUrl ?? '',
              name: step?.agent?.name ?? '',
              room: step.room,
              group: step?.group,
            },
          });
          this.notifyDesktopIncomingCall(step?.agent?.name);
        }
      });
  }

  private listenCreateChatStepCallInitiated() {
    this.createChatStepCallInitiatedSubscription?.unsubscribe();
    this.createChatStepCallInitiatedSubscription = this.chatSocketService.createChatStepCallInitiatedSubject$.subscribe(
      (step) => {
        if (step) {
          const agent = step?.agent as UserI;

          this.dialog.open(JitsiIncomingCallComponent, {
            panelClass: 'custom-dialog-v2',
            hasBackdrop: false,
            data: {
              avatarUrl: agent?.photo ?? '',
              name: agent?.name ?? '',
              room: step.room,
              chat: step?.chat,
            },
          });
          this.notifyDesktopIncomingCall(agent?.name, step?.chat);
        }
      }
    );
  }

  private listenNewChatStep() {
    this.newChatStepSubscription?.unsubscribe();
    this.newChatStepSubscription = this.chatSocketService.newChatStep$.subscribe((data) => {
      if (data) {
        this.chatStoreService.addStepInChat(data.chatId, data.step);
      }
    });
  }

  private async notifyDesktopNewMessage(message: MessageI): Promise<void> {
    // Mirror the bell (setNotificationsOnNewMessage): only notify for chats in the user's own
    // store/view. The socket delivers messages for every joined team room, not just the agent's.
    const chat = await this.chatStoreService.existsChatInStore({ _id: message.chat as string });
    if (!chat) {
      return;
    }
    const title = (message?.sender as any)?.name || this.translateService.instant('NOTIFICATIONS.DESKTOP.NEW_MESSAGE');
    this.desktopNotificationService.notify({
      type: 'newMessage',
      title,
      body: this.buildMessagePreview(message),
      tag: message.chat,
      onClick: () => this.openChatFromNotification(message.chat),
    });
  }

  private async notifyDesktopInternalNote(message: MessageI): Promise<void> {
    // Same scope as the bell: an internal note on a chat outside the agent's view must not notify.
    const chat = await this.chatStoreService.existsChatInStore({ _id: message.chat as string });
    if (!chat) {
      return;
    }
    const agentName = (message?.sender as any)?.name;
    const body = agentName
      ? this.translateService.instant('NOTIFICATIONS.DESKTOP.INTERNAL_NOTE_BODY', { agent: agentName })
      : this.buildMessagePreview(message);
    this.desktopNotificationService.notify({
      type: 'internalNote',
      title: this.translateService.instant('NOTIFICATIONS.DESKTOP.INTERNAL_NOTE'),
      body,
      tag: message.chat,
      onClick: () => this.openChatFromNotification(message.chat),
    });
  }

  private async notifyDesktopGroupMessage(groupId: string, message: any): Promise<void> {
    if (this.userHelperService.verifyIfUserIsCurrentUser(message?.sender?._id)) {
      return;
    }
    // Same scope as the bell: only notify for groups in the agent's own store/view.
    const groupExists = await this.groupStoreService.checkGroupExistsInStore(groupId);
    if (!groupExists) {
      return;
    }
    const senderName = message?.sender?.name;
    const preview = this.buildMessagePreview(message);
    const body = senderName ? `${senderName}: ${preview}` : preview;
    this.desktopNotificationService.notify({
      type: 'groupMessage',
      title: this.translateService.instant('NOTIFICATIONS.DESKTOP.GROUP_MESSAGE'),
      body,
      tag: groupId,
      onClick: () => this.navigateToChatPanel(),
    });
  }

  private notifyDesktopNewAttendance(chat: ChatI): void {
    const contact = chat?.contact?.name || this.translateService.instant('NOTIFICATIONS.DESKTOP.NEW_ATTENDANCE');
    const channel = (chat?.channel as any)?.name;
    const body = channel
      ? this.translateService.instant('NOTIFICATIONS.DESKTOP.NEW_ATTENDANCE_BODY', { contact, channel })
      : contact;
    this.desktopNotificationService.notify({
      type: 'newAttendance',
      title: this.translateService.instant('NOTIFICATIONS.DESKTOP.NEW_ATTENDANCE'),
      body,
      tag: chat._id,
      onClick: () => this.openChatFromNotification(chat._id, chat),
    });
  }

  private notifyDesktopIncomingCall(callerName?: string, chatId?: string): void {
    this.desktopNotificationService.notify({
      type: 'incomingCall',
      title: this.translateService.instant('NOTIFICATIONS.DESKTOP.INCOMING_CALL'),
      body: callerName,
      tag: 'incoming-call',
      onClick: chatId ? () => this.openChatFromNotification(chatId) : undefined,
    });
  }

  private buildMessagePreview(message: any): string {
    const content = message?.content;
    const text = content?.message ?? content?.text ?? content?.caption ?? message?.caption;
    const preview = text ? String(text).trim() : '';
    return preview || this.translateService.instant('NOTIFICATIONS.DESKTOP.MEDIA');
  }

  private async openChatFromNotification(chatId?: string, chat?: ChatI): Promise<void> {
    if (!chatId) {
      return;
    }
    try {
      const target = chat ?? (await firstValueFrom(this.chatService.findChatById(chatId)));
      if (!target) {
        return;
      }
      this.chatStoreService.populateStore([target], true);
      await this.chatStoreService.setCurrentChatDirectOpen(target);
      await this.navigateToChatPanel();
    } catch {
      // best-effort deep-link; a fetch/route failure must not break notification handling
    }
  }

  private async navigateToChatPanel(): Promise<void> {
    const chatRoute = `/${RoutesEnum.PANEL}/${RoutesEnum.CHAT}`;
    if (this.router.url !== chatRoute) {
      await this.router.navigate([chatRoute]);
    }
  }
}
